/**
 * Overview — the Customer landing: one row per site (Customer menu redesign, Phase 1).
 *
 * Meridian's answer to Meraki's Organization > Overview "Networks" table. It answers "which of
 * this customer's sites needs me" without walking the Health page site by site, and is built
 * for customers with 100+ sites:
 *
 *   1. A SUMMARY LINE first ("12 sites · 48 devices · 3 offline · 57 clients now"), so the
 *      answer to "is anything wrong" never depends on reading every row.
 *   2. WORST FIRST. The default order is health, then offline, then alerts -- the same
 *      editorial triage order as the Customers table, so the two screens read alike.
 *   3. FILTER BEFORE SCROLL. Client-side search (site, location, customer) and quick chips
 *      (Needs attention / Offline). The payload is one small row per site, so filtering in the
 *      browser is instant and costs no round trip.
 *   4. 100+ ROWS. DataGrid `pageSize` renders 50 sorted rows at a time with "Show more", so a
 *      large customer neither paints hundreds of rows at once nor sorts only a slice.
 *   5. CLICK A ROW -> that site. The header scope narrows to the site (session + URL, which
 *      ScopeSync keeps in agreement) and the operator lands on the site's Health.
 *
 * The ONLINE column is the current online ratio (devices online / devices). It is not a 24h
 * availability history: that bar is deferred (Phase 3) until a per-site rollup exists, and the
 * footnote says so rather than drawing a bar the data cannot back.
 *
 * Colour: the status ramp appears only for operational state (health stripe, StatusPill, the
 * online meter, offline/alert counts); brand violet only for interactive affordances (chips,
 * Export). Tokens throughout, so light and dark themes follow automatically.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useSitesOverview } from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { CountLink } from "@/components/data/CountLink";
import { alertsHref, offlineDevicesHref } from "@/lib/alertGuide";
import {
  HEALTH_RANK,
  filterRows,
  firmwareLabel,
  summarize,
  summaryLine,
  toCsv,
  triageSort,
  type OverviewFilter,
} from "@/lib/siteOverview";
import type { SiteOverviewRow } from "@/types/domain";
import { QuarantinePanel } from "@/components/quarantine/QuarantinePanel";
import { isQuarantineOrg } from "@/lib/quarantine";

/** Rows per "page" before Show more (DataGrid pageSize). */
const PAGE_SIZE = 50;

/**
 * The route element. The quarantine holding area is not a customer, so it gets its own panel
 * (devices waiting to be claimed + Assign) instead of the per-site customer table (2026-10-19).
 */
export default function Overview() {
  const quarantine = useSession((s) => isQuarantineOrg(s.currentOrg()));
  return quarantine ? <QuarantinePanel /> : <CustomerOverview />;
}

function CustomerOverview() {
  const { data, isLoading, error } = useSitesOverview();
  const orgId = useSession((s) => s.currentOrgId);
  const customerName = useSession((s) => s.currentOrg()?.name ?? null);
  const setOrg = useSession((s) => s.setOrg);
  const setSite = useSession((s) => s.setSite);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<OverviewFilter>("all");

  // Quarantine placeholder sites are not customer sites: never a row, never in "N sites". Only
  // reachable when no customer is selected (the cross-customer view); a selected quarantine
  // org renders QuarantinePanel instead.
  const me = useSession((s) => s.me);
  const quarantineIds = useMemo(
    () => new Set((me?.orgs ?? []).filter(isQuarantineOrg).map((o) => o.id)),
    [me],
  );
  const all = useMemo(
    () => (data?.items ?? []).filter((r) => !quarantineIds.has(r.org_id)),
    [data, quarantineIds],
  );
  const totals = useMemo(() => summarize(all), [all]);
  const rows = useMemo(() => triageSort(filterRows(all, query, filter)), [all, query, filter]);
  // The Customer column earns its width only when the rows span several customers (an
  // msp_admin view); inside one customer it would repeat the header chip on every row.
  const multiCustomer = totals.customers > 1;

  /** Narrow the header scope to the site and open its Health. */
  const open = (r: SiteOverviewRow) => {
    if (r.org_id !== orgId) setOrg(r.org_id);
    setSite(r.site_id);
    navigate(`/o/${r.org_id}/s/${r.site_id}/dashboard`);
  };

  /** Download the CURRENT view (search + filter applied, triage order) as CSV. */
  const exportCsv = () => {
    const csv = toCsv(rows, multiCustomer);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = (customerName ?? "customer").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    a.href = url;
    a.download = `${slug}-sites-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo<ColumnDef<SiteOverviewRow, unknown>[]>(
    () => [
      {
        id: "site",
        header: "Site",
        accessorFn: (r) => r.site_name,
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <div className="flex min-w-0 items-center gap-2">
              {/* A severity stripe reads before any number does (same as Customers). */}
              <HealthStripe health={r.health} empty={r.devices_total === 0} />
              <div className="min-w-0">
                <div className="truncate font-medium text-ink">{r.site_name}</div>
                {r.location && (
                  <div className="truncate text-xs text-ink-subtle">{r.location}</div>
                )}
              </div>
            </div>
          );
        },
      },
      ...(multiCustomer
        ? [
            {
              id: "customer",
              header: "Customer",
              accessorFn: (r: SiteOverviewRow) => r.org_name,
              cell: (ctx: { row: { original: SiteOverviewRow } }) => (
                <span className="text-ink-muted">{ctx.row.original.org_name}</span>
              ),
            } as ColumnDef<SiteOverviewRow, unknown>,
          ]
        : []),
      {
        id: "health",
        header: "Health",
        accessorFn: (r) => HEALTH_RANK[r.health],
        cell: (ctx) => {
          const r = ctx.row.original;
          return r.devices_total === 0 ? (
            <StatusPill status="unknown">No devices</StatusPill>
          ) : (
            <StatusPill status={r.health} />
          );
        },
      },
      {
        id: "online",
        header: "Online",
        // Empty sites sort below every real ratio (-1), not as "0% online".
        accessorFn: (r) => r.online_pct ?? -1,
        cell: (ctx) => <OnlineMeter pct={ctx.row.original.online_pct} />,
      },
      {
        id: "devices",
        header: "Devices",
        accessorFn: (r) => r.devices_total,
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className="tabular-nums text-ink">
              {r.devices_online}
              <span className="text-ink-subtle">/{r.devices_total}</span>
            </span>
          );
        },
      },
      {
        id: "offline",
        header: "Offline",
        accessorFn: (r) => r.devices_offline,
        cell: (ctx) => {
          const r = ctx.row.original;
          // Opens this site's Devices list filtered to offline (OP#1854).
          return (
            <CountLink
              n={r.devices_offline}
              to={offlineDevicesHref(r.org_id, r.site_id)}
              tone="warn"
              label={`offline devices at ${r.site_name}`}
            />
          );
        },
      },
      {
        id: "clients",
        header: "Clients now",
        accessorFn: (r) => r.clients_now,
        cell: (ctx) => (
          <span className="tabular-nums text-ink">{ctx.row.original.clients_now}</span>
        ),
      },
      {
        id: "alerts",
        header: "Alerts",
        accessorFn: (r) => r.alerts_open,
        cell: (ctx) => {
          const r = ctx.row.original;
          // Opens this site's open-alerts list (OP#1854).
          return (
            <CountLink
              n={r.alerts_open}
              to={alertsHref(r.org_id, r.site_id)}
              tone={r.alerts_critical > 0 ? "critical" : "warn"}
              label={`open alerts at ${r.site_name}`}
            />
          );
        },
      },
      {
        id: "firmware",
        header: "Firmware",
        accessorFn: (r) => r.firmware.differs,
        cell: (ctx) => <FirmwareCell fw={ctx.row.original.firmware} />,
      },
    ],
    [multiCustomer],
  );

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle={customerName ? `Every site for ${customerName}` : "Every site you can see"}
        actions={
          all.length > 0 ? (
            <Button variant="secondary" onClick={exportCsv} disabled={rows.length === 0}>
              Export CSV
            </Button>
          ) : undefined
        }
      />

      {error && <ErrorBanner error={error} />}

      {all.length > 0 && (
        <>
          <p role="status" className="text-sm text-ink">
            {/* The numbers are plain ink; only a non-zero offline count earns the warn tone. */}
            <span className="tabular-nums">{summaryLine(totals)}</span>
            {totals.attention > 0 && (
              <span className="text-ink-muted">
                {" "}— <span className="font-medium text-warn">{totals.attention}</span>{" "}
                {totals.attention === 1 ? "needs" : "need"} attention
              </span>
            )}
          </p>

          <div className="mb-3 mt-3 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="overview-search">
              Search sites
            </label>
            <input
              id="overview-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={multiCustomer ? "Search sites or customers" : "Search sites"}
              className="min-h-touch w-full max-w-xs rounded border border-line bg-surface px-3 text-sm text-ink placeholder:text-ink-subtle"
            />
            <div className="flex flex-wrap items-center gap-1">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
                All {totals.sites}
              </FilterChip>
              <FilterChip
                active={filter === "attention"}
                onClick={() => setFilter("attention")}
                tone={totals.attention > 0 ? "warn" : undefined}
              >
                Needs attention {totals.attention}
              </FilterChip>
              {totals.offline > 0 && (
                <FilterChip active={filter === "offline"} onClick={() => setFilter("offline")}>
                  With offline devices
                </FilterChip>
              )}
            </div>
            {rows.length !== all.length && (
              <span className="text-xs text-ink-muted">
                {rows.length} of {all.length}
              </span>
            )}
          </div>
        </>
      )}

      {data && all.length === 0 ? (
        <EmptyState
          title="No sites yet"
          hint="Add a site, then onboard devices into it. Each site gets its own row here."
          action={
            orgId ? (
              <Link
                to={`/o/${orgId}/sites`}
                className="inline-flex min-h-touch items-center rounded px-4 text-sm font-medium text-brand hover:bg-brand-soft"
              >
                Go to Sites
              </Link>
            ) : undefined
          }
        />
      ) : (
        <DataGrid
          data={rows}
          columns={columns}
          rowKey={(r) => r.site_id}
          dense
          isLoading={isLoading}
          onRowActivate={open}
          pageSize={PAGE_SIZE}
          pageResetKey={`${query}|${filter}`}
          empty={
            <span>
              {filter === "attention"
                ? "Every site is healthy."
                : filter === "offline"
                  ? "No site has offline devices."
                  : "No site matches that search."}
            </span>
          }
          renderCard={(r) => <SiteCard row={r} showCustomer={multiCustomer} />}
        />
      )}

      {all.length > 0 && (
        <p className="mt-3 text-xs text-ink-subtle">
          Online is the share of devices connected right now. A 24-hour availability history
          per site is not shown yet. Select a site to open its Health page.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

/** The leading severity stripe; muted for a site with no devices (nothing to be healthy). */
function HealthStripe({ health, empty }: { health: SiteOverviewRow["health"]; empty: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "h-5 w-1 shrink-0 rounded-full",
        empty
          ? "bg-line"
          : health === "critical"
            ? "bg-critical"
            : health === "warn"
              ? "bg-warn"
              : "bg-healthy/40",
      )}
    />
  );
}

/**
 * The online meter: a short bar plus the percentage. Operational state, so it uses the status
 * ramp -- full is healthy, partial is warn, nothing online is critical. A site with no devices
 * shows a dash: "0 of 0 online" is not an outage.
 */
function OnlineMeter({ pct, suffix }: { pct: number | null; suffix?: string }) {
  if (pct === null) return <span className="text-ink-subtle">—</span>;
  const tone = pct >= 100 ? "bg-healthy" : pct === 0 ? "bg-critical" : "bg-warn";
  return (
    <span className="inline-flex items-center gap-2" title={`${pct}% of devices online now`}>
      <span
        aria-hidden
        className="relative h-1.5 w-16 overflow-hidden rounded-full bg-surface-2"
      >
        <span
          className={cn("absolute inset-y-0 left-0 rounded-full", tone)}
          // A sliver stays visible at 0% so the critical tone still reads.
          style={{ width: `${Math.max(pct, 4)}%` }}
        />
      </span>
      {suffix ? (
        <span className="tabular-nums text-ink">
          {pct}% <span className="text-ink-muted">{suffix}</span>
        </span>
      ) : (
        <span className="w-9 text-right tabular-nums text-ink">{pct}%</span>
      )}
    </span>
  );
}

/** Firmware verdict. Off-baseline is drift (warn), not "upgrade available" -- see siteOverview. */
function FirmwareCell({ fw }: { fw: SiteOverviewRow["firmware"] }) {
  const label = firmwareLabel(fw);
  if (fw.status === "none") return <span className="text-ink-subtle">{label}</span>;
  if (fw.status === "off_baseline")
    return (
      <span className="text-warn" title="Running a build other than the approved baseline">
        {label}
      </span>
    );
  if (fw.status === "current") return <span className="text-ink">{label}</span>;
  return <span className="text-ink-muted">{label}</span>;
}

function FilterChip({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone?: "warn";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "min-h-touch rounded-full border px-3 text-xs transition-colors",
        active
          ? "border-brand bg-brand/10 text-brand"
          : "border-line text-ink-muted hover:bg-surface-2",
        !active && tone === "warn" && "text-warn",
      )}
    >
      {children}
    </button>
  );
}

/** The sub-`lg` card DataGrid reflows to. Same facts, stacked, tablet-first. */
function SiteCard({ row: r, showCustomer }: { row: SiteOverviewRow; showCustomer: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <HealthStripe health={r.health} empty={r.devices_total === 0} />
          <div className="min-w-0">
            <div className="truncate font-medium text-ink">{r.site_name}</div>
            <div className="truncate text-xs text-ink-muted">
              {[showCustomer ? r.org_name : null, r.location].filter(Boolean).join(" · ") ||
                " "}
            </div>
          </div>
        </div>
        {r.devices_total === 0 ? (
          <StatusPill status="unknown">No devices</StatusPill>
        ) : (
          <StatusPill status={r.health} />
        )}
      </div>
      <OnlineMeter pct={r.online_pct} suffix="online" />
      <dl className="grid grid-cols-4 gap-2">
        <Metric label="Devices" value={`${r.devices_online}/${r.devices_total}`} />
        <Metric
          label="Offline"
          value={r.devices_offline}
          tone={r.devices_offline ? "warn" : undefined}
          to={offlineDevicesHref(r.org_id, r.site_id)}
          linkLabel={`offline devices at ${r.site_name}`}
        />
        <Metric label="Clients" value={r.clients_now} />
        <Metric
          label="Alerts"
          value={r.alerts_open}
          tone={r.alerts_open ? (r.alerts_critical ? "critical" : "warn") : undefined}
          to={alertsHref(r.org_id, r.site_id)}
          linkLabel={`open alerts at ${r.site_name}`}
        />
      </dl>
      {r.firmware.status !== "none" && (
        <div className="text-xs text-ink-muted">
          Firmware: <FirmwareCell fw={r.firmware} />
        </div>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
  to,
  linkLabel,
}: {
  label: string;
  value: number | string;
  tone?: "warn" | "critical";
  /** When set and the value is a non-zero number, the value links here (OP#1854). */
  to?: string;
  linkLabel?: string;
}) {
  if (to && typeof value === "number" && tone) {
    return (
      <div>
        <dt className="text-xs text-ink-muted">{label}</dt>
        <dd className="mt-0.5 text-lg font-semibold">
          <CountLink n={value} to={to} tone={tone} label={linkLabel ?? label} />
        </dd>
      </div>
    );
  }
  const toneClass = tone === "warn" ? "text-warn" : tone === "critical" ? "text-critical" : "text-ink";
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className={cn("mt-0.5 text-lg font-semibold tabular-nums", toneClass)}>{value}</dd>
    </div>
  );
}
